import { isNil, sortBy } from "lodash";
import {
    AddOn,
    AmountType,
    CatalogItem,
    CatalogItemDisplaySection,
    CatalogItemSequences,
    CatalogItemSequenceType,
    CatalogType,
    LineItemType,
    ProductCatalogItem,
    UnitType,
    VariableAmountType,
} from "types";
import { xolaUri } from "../api/xolaUri";
import { getPlaceholderImageUrl } from "./Product";
import { ChoicesItem } from "@/components/Product/GlobalSettings/AddOns/AddOnTypes";

export const getAddOnPriceRange = (addOn: AddOn) => {
    let lowest: number | null = null;
    let highest: number | null = null;
    let priceType: AmountType | VariableAmountType = AmountType.ABSOLUTE;
    if ("price" in addOn) {
        lowest = addOn.price;
        highest = addOn.price;
        priceType = addOn.priceType;
    } else {
        const { items }: CatalogItem = addOn;
        if (items) {
            priceType = items[0].priceType;
            for (const item of items) {
                if (item.priceType !== priceType) {
                    priceType = VariableAmountType.VARIABLE;
                }

                if (!lowest || item.price < lowest) {
                    lowest = item.price;
                }

                if (!highest || item.price > highest) {
                    highest = item.price;
                }
            }
        }
    }

    return {
        price: { lowest: lowest, highest: highest },
        priceType: priceType,
    };
};

export const isChoiceAddOn = (catalogItem: ProductCatalogItem) => {
    return isAddonCatalogItem(catalogItem) && catalogItem.type === CatalogType.CHOICES;
};

export const isAddonCatalogItem = (catalogItem: ProductCatalogItem) => {
    return (
        catalogItem.unitType === UnitType.ADDON ||
        catalogItem.unitType === UnitType.PICKUP ||
        catalogItem.template?.type === LineItemType.ADDON
    );
};

export const getDefaultPhotoUrl = (catalogItem: CatalogItem, size = "medium_square") => {
    if (!catalogItem.id || !catalogItem.photo) {
        return getPlaceholderImageUrl(size);
    }

    let uri = `${xolaUri}/api/catalogItems/${catalogItem.id}/photo?size=${size}`;

    if (catalogItem.photo?.id) {
        uri += `&photoId=${catalogItem.photo.id}`;
    }

    return uri;
};

export const isNewDemographicCatalogItem = (item: CatalogItem) => item.unitType === UnitType.DEMOGRAPHIC && !item.id;

interface GetResultingSequenceProps {
    destinationIndex: number;
    sourceIndex: number;
    productCatalogItems: Array<ProductCatalogItem | CatalogItem | ChoicesItem>;
    type: CatalogItemSequenceType;
}

export const getResultingSequence = ({
    destinationIndex,
    sourceIndex,
    productCatalogItems,
    type,
}: GetResultingSequenceProps) => {
    if (destinationIndex < sourceIndex && productCatalogItems) {
        const previousCatalogItemSequence = productCatalogItems[destinationIndex - 1]?.sequence;
        const currentCatalogItemSequence = productCatalogItems[destinationIndex]?.sequence;

        if (previousCatalogItemSequence && currentCatalogItemSequence) {
            return Math.floor((previousCatalogItemSequence + currentCatalogItemSequence) / 2);
        }

        if (!previousCatalogItemSequence && currentCatalogItemSequence) {
            if (type === CatalogItemSequenceType.ADDON) {
                return Math.floor((CatalogItemSequences.ADDON_SEQUENCE_START + currentCatalogItemSequence) / 2);
            }
            if (type === CatalogItemSequenceType.EQUIPMENT) {
                return Math.floor((CatalogItemSequences.EQUIPMENT_SEQUENCE_START + currentCatalogItemSequence) / 2);
            }
            return Math.floor(currentCatalogItemSequence / 2);
        }
    } else {
        const nextCatalogItemSequence = productCatalogItems[destinationIndex + 1]?.sequence;
        const currentCatalogItemSequence = productCatalogItems[destinationIndex]?.sequence;

        if (nextCatalogItemSequence && currentCatalogItemSequence) {
            return Math.floor((nextCatalogItemSequence + currentCatalogItemSequence) / 2);
        }

        if (!nextCatalogItemSequence && currentCatalogItemSequence) {
            return currentCatalogItemSequence + CatalogItemSequences.STEP;
        }
    }
};

export const populateMissingSequences = (
    productCatalogItems: Array<ProductCatalogItem | CatalogItem | ChoicesItem>,
): Array<ProductCatalogItem | CatalogItem | ChoicesItem> => {
    const items = sortBy(productCatalogItems, "sequence");
    const result: Array<ProductCatalogItem | CatalogItem | ChoicesItem> = [];

    for (const item of items) {
        if (item.sequence) {
            result.push(item);
        } else {
            const previousCatalogItemSequence = result[result.length - 1]?.sequence ?? 0;
            const isDemographic =
                (item as CatalogItem).unitType === UnitType.DEMOGRAPHIC ||
                (item as CatalogItem).unitType === UnitType.DEMOGRAPHIC_SECONDARY;
            const isEquipment = (item as CatalogItem).unitType === UnitType.EQUIPMENT;

            const newSequence =
                previousCatalogItemSequence !== 0 || isDemographic
                    ? previousCatalogItemSequence + CatalogItemSequences.STEP
                    : isEquipment
                    ? CatalogItemSequences.EQUIPMENT_SEQUENCE_START + CatalogItemSequences.STEP
                    : CatalogItemSequences.ADDON_SEQUENCE_START + CatalogItemSequences.STEP;

            const newItem = {
                ...item,
                sequence: newSequence,
            };

            result.push(newItem);
        }
    }

    return result;
};

export const isQuestionnaireAddOn = (catalogItem: CatalogItem) => {
    return !!catalogItem.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
};

export const isMatchingGuestCount = (catalogItem: CatalogItem) => !isNil(catalogItem.quantitySource);

const initialDemographicsSequence = CatalogItemSequences.DEMOGRAPHICS_SEQUENCE_START + CatalogItemSequences.STEP;

export const roundDownSequence = (maxSequence: number, initialSequence: number = initialDemographicsSequence) =>
    Math.floor(maxSequence / initialSequence) * initialSequence;
